import React, { useCallback, useEffect, useContext } from 'react';
import { HeaderCellButton } from './HeaderCellButton';
import { Popover, usePopover, PopoverScreen } from '@trello/nachos/popover';
import { SavedViewContext, SavedViewEditable } from './SavedViewContext';
import { useListHeaderCellQuery } from './ListHeaderCellQuery.generated';
import { forTemplate } from '@trello/i18n';

import { Analytics } from '@trello/atlassian-analytics';
import { ListFilterPopoverContent } from './filters/ListFilterPopoverContent';

const format = forTemplate('multi_board_table_view');

enum Screen {
  ListFilterScreen,
}

interface ListHeaderCellProps {
  idBoards: string[];
  idOrg: string;
}

const ListHeaderCellEditable: React.FC<
  ListHeaderCellProps & { savedView: SavedViewEditable }
> = ({ idBoards, idOrg, savedView }) => {
  const { toggle, triggerRef, popoverProps } = usePopover<HTMLDivElement>({
    initialScreen: Screen.ListFilterScreen,
  });

  useEffect(() => {
    if (popoverProps.isVisible) {
      Analytics.sendScreenEvent({
        name: 'listFilterInlineDialog',
        containers: {
          organization: {
            id: idOrg,
          },
        },
      });
    }
  }, [popoverProps.isVisible, idOrg]);

  const { data } = useListHeaderCellQuery({
    fetchPolicy: 'cache-only',
    variables: {
      idOrg,
      idBoards,
    },
  });

  if (!data?.organization?.boards) {
    throw new Error('Failed to get list header cell details from cache.');
  }

  const toggleListPopover = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      source: 'multiBoardTableViewScreen',
      buttonName: 'filterTableByListButton',
      containers: {
        organization: {
          id: idOrg,
        },
      },
    });

    toggle();
  }, [toggle, idOrg]);

  return (
    <>
      <HeaderCellButton
        heading={format('list')}
        onClick={toggleListPopover}
        isClickable={true}
        isFilterActive={savedView.filters.list.filterLength() !== 0}
        isSortingActive={false}
        forwardedRef={triggerRef}
        isActive={popoverProps.isVisible}
      />
      <Popover
        title={format('list')}
        dangerous_disableHideOnUrlSearchParamsChange
        dontOverlapAnchorElement
        {...popoverProps}
      >
        <PopoverScreen id={Screen.ListFilterScreen}>
          <ListFilterPopoverContent
            popoverTarget="headerCell"
            idOrg={idOrg}
            showResetButton={true}
            listFilter={savedView.filters.list}
            setListFilter={savedView.setFilter}
            boards={data.organization?.boards}
          ></ListFilterPopoverContent>
        </PopoverScreen>
      </Popover>
    </>
  );
};

export const ListHeaderCell: React.FunctionComponent<ListHeaderCellProps> = ({
  idBoards,
  idOrg,
}) => {
  const { savedView } = useContext(SavedViewContext);

  return savedView.editable ? (
    <ListHeaderCellEditable
      idBoards={idBoards}
      idOrg={idOrg}
      savedView={savedView}
    />
  ) : (
    <HeaderCellButton
      heading={format('list')}
      isClickable={false}
      isFilterActive={savedView.filters.list.filterLength() !== 0}
      isSortingActive={false}
    />
  );
};
